import { useMemo, useState } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { AddressFieldMaxLength } from "@shared";
import { insuranceNumberValidation } from "@shared";
import { ClientPersonalDataMaxLength } from "@shared";
import { phoneNumberValidation } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import {
  ClientResponse,
  ClientSearchResult,
  ContactDetailType,
  CreateClientRequest,
  DropdownCategory,
  clientsApi,
} from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { DropdownCategoryMaxLength } from "@/common/constants/dropdownCategoryMaxLength";
import { EmailPattern, ZipCodePattern } from "@/common/helper/validationPatterns";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { CreateClientType } from "@/pages/clients/client/components/create-client/CreateClient.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

type CreateClientValidationType = {
  [Property in keyof CreateClientType]?: Yup.AnySchema | Yup.Reference;
};

export function useClientCreation(onClose: () => void, onClientCreated?: (client: ClientResponse) => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const dropdownOptions = useDropdownOptions();

  // Potential duplicates surfaced by the last check, keyed by the name/birthday combination that
  // produced them. The list is only considered current while the form still holds that same
  // combination; editing any of those fields invalidates it (see `duplicates` below) so the next
  // "Anlegen" runs a fresh check.
  const [duplicateResult, setDuplicateResult] = useState<{ key: string; matches: ClientSearchResult[] } | null>(null);

  // MUI X DatePicker emits an Invalid Date (not null) for incomplete/impossible input, and
  // toISOString() throws on it — only serialize a genuinely valid date.
  const isValidDate = (date: Date | null): date is Date => date != null && !isNaN(date.getTime());

  const matchKey = (firstName: string, lastName: string, birthday: Date | null) =>
    `${firstName}|${lastName}|${isValidDate(birthday) ? birthday.toISOString() : ""}`;

  const checkDuplicatesMutation = useMutation({
    mutationFn: (request: { firstName: string; lastName: string; birthday: Date | null }) =>
      clientsApi.checkClientDuplicates({
        firstName: request.firstName,
        lastName: request.lastName,
        birthday: request.birthday ?? undefined,
      }),
  });

  const createClientMutation = useMutation({
    mutationFn: (request: CreateClientRequest) =>
      clientsApi.createClient(
        {
          createClientRequest: request,
        },
        // onError toasts every failure itself; without this the global middleware would toast too.
        withLocalErrorHandling
      ),

    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] });
      showSuccess(t("clients.create.successfullyAdded"));
      onClose();
      onClientCreated?.(client);
    },
    // The backend's own messages are English technical texts; the dialog stays open so the user can retry.
    onError: () => {
      showError(t("clients.create.saveFailed"));
    },
  });

  const validationSchema = useMemo(() => {
    const minimalBirthDate = new Date(new Date().setFullYear(new Date().getFullYear() - 120));

    return Yup.object().shape<CreateClientValidationType>({
      initialRegistrationDate: Yup.date()
        .typeError(t("common.validation.invalidDateFormat"))
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.creationDate") }))
        .test("min-today", t("common.validation.dateMinToday"), (value) => {
          if (!(value instanceof Date) || isNaN(value.getTime())) {
            return false;
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return value >= today;
        }),
      contactSource: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.contactSource, ({ max }) => t("common.validation.maxLength", { max })),
      comment: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.comment, ({ max }) => t("common.validation.maxLength", { max })),
      salutation: Yup.string()
        .nullable()
        .max(DropdownCategoryMaxLength[DropdownCategory.Salutation], ({ max }) =>
          t("common.validation.maxLength", { max })
        ),
      title: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.title, ({ max }) => t("common.validation.maxLength", { max })),
      firstName: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.name") }))
        .max(ClientPersonalDataMaxLength.firstName, ({ max }) => t("common.validation.maxLength", { max })),
      lastName: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("clients.create.lastName") }))
        .max(ClientPersonalDataMaxLength.lastName, ({ max }) => t("common.validation.maxLength", { max })),
      formerLastName: Yup.string()
        .nullable()
        .max(ClientPersonalDataMaxLength.formerLastName, ({ max }) => t("common.validation.maxLength", { max })),
      street: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.street") }))
        .max(ClientPersonalDataMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
      houseNumber: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.houseNumber") }))
        .max(AddressFieldMaxLength.houseNumber, ({ max }) => t("common.validation.maxLength", { max })),
      zipCode: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.zipCode") }))
        .matches(ZipCodePattern, t("clients.create.invalidZipCode"))
        .min(4, ({ min }) => t("common.validation.minLength", { min }))
        .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
      city: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.city") }))
        .max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
      country: Yup.string()
        .required(t("common.validation.fieldRequired", { fieldName: t("common.address.country") }))
        .max(ClientPersonalDataMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
      email: Yup.string()
        .nullable()
        .optional()
        .matches(EmailPattern, t("clients.create.invalidEmail"))
        .max(ClientPersonalDataMaxLength.contactDetail.value, ({ max }) => t("common.validation.maxLength", { max })),
      phone: phoneNumberValidation(t)
        .nullable()
        .optional()
        .max(ClientPersonalDataMaxLength.contactDetail.value, ({ max }) => t("common.validation.maxLength", { max })),
      birthday: Yup.date()
        .nullable()
        .typeError(t("common.validation.invalidDateFormat"))
        .min(minimalBirthDate, t("clients.create.birthdayTooOld"))
        .max(new Date(), t("clients.create.dateMaxToday")),
      employmentStatus: Yup.string().nullable().optional(),
      ...insuranceNumberValidation(t),
    });
  }, [t]);

  const formik = useFormikWithDirtyForm<CreateClientType>({
    initialValues: {
      initialRegistrationDate: new Date(),
      contactSource: null,
      comment: null,
      salutation: null,
      title: null,
      firstName: "",
      lastName: "",
      formerLastName: null,
      street: "",
      houseNumber: "",
      zipCode: "",
      city: "",
      country: "",
      email: null,
      phone: null,
      birthday: null,
      insuranceNumber: null,
      employmentStatus: null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      // First "Anlegen": run the duplicate check. If matches are found, surface them and switch the
      // button to "Trotzdem anlegen" instead of creating. A second submit (duplicates already shown
      // for this exact name/birthday) skips the check and creates anyway.
      const key = matchKey(values.firstName, values.lastName, values.birthday);
      const alreadyShown = duplicateResult?.key === key && duplicateResult.matches.length > 0;

      if (!alreadyShown) {
        const result = await checkDuplicatesMutation.mutateAsync({
          firstName: values.firstName,
          lastName: values.lastName,
          birthday: values.birthday,
        });

        if (result.data.length > 0) {
          setDuplicateResult({ key, matches: result.data });
          return;
        }
      }

      const createClientRequest: CreateClientRequest = {
        initialRegistrationDate: values.initialRegistrationDate,
        contactSource: values.contactSource,
        comment: values.comment,
        salutation: values.salutation,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        formerLastName: values.formerLastName,
        street: values.street,
        houseNumber: values.houseNumber,
        zipCode: values.zipCode,
        city: values.city,
        country: values.country,
        contactDetails: [
          ...(values.email ? [{ id: null, type: ContactDetailType.Email, value: values.email, comment: null }] : []),
          ...(values.phone ? [{ id: null, type: ContactDetailType.Phone, value: values.phone, comment: null }] : []),
        ],
        birthday: values.birthday,
        insuranceNumber: values.insuranceNumber,
        employmentStatus: values.employmentStatus,
      };

      await createClientMutation.mutateAsync(createClientRequest);
    },
  });

  // The shown duplicates are only valid while the form still holds the name/birthday they were found
  // for; editing any of those fields invalidates them so the button reverts to "Anlegen" and the next
  // submit re-checks.
  const currentKey = matchKey(formik.values.firstName, formik.values.lastName, formik.values.birthday);
  const duplicates = duplicateResult?.key === currentKey ? duplicateResult.matches : null;

  return { formik, dropdownOptions, duplicates };
}
